// components/SkopaTargets.tsx
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/client/components/shad/empty";
import {
  IconCancel,
  IconCrosshair,
  IconCurrentLocation,
  IconDeselect,
  IconSkull,
  IconViewfinderOff,
} from "@tabler/icons-react";
import { useAircraftSelection } from "./hooks/useAircraft";
import { useState } from "react";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemFooter,
  ItemTitle,
} from "@/client/components/shad/item";
import { Button } from "@/client/components/shad/button";
import { getAltitudeInFeet } from "@/client/lib/getAltitudeInFeet";
import { getSpeedInKnots } from "@/client/lib/getSpeedInKnots";
import {
  ButtonGroup,
  ButtonGroupSeparator,
} from "@/client/components/shad/button-group";
import { getHeadingInDegrees } from "@/client/lib/getHeadingInDegrees";
import type { SkopaTarget, KVP } from "@/client/map/types";
import { useTopic } from "@/hooks/useTopic";
import type { TargetData } from "./SkopaTargetList";

export const SkopaTargets = () => {
  const { selectedTargets } = useAircraftSelection();

  return (
    <div className="space-y-1 pr-1">
      {selectedTargets.map((target) => (
        <SkopaTargetItem key={target.track_id} target={target} />
      ))}
    </div>
  );
};

const SkopaTargetItem = ({ target }: { target: SkopaTarget }) => {
  const [interceptActive, setInterceptActive] = useState(false);
  const { deselect } = useAircraftSelection();

  const { sendCommand } = useTopic<TargetData>("skopa/intercept");

  const startIntercept = () => {
    setInterceptActive(true);
    sendCommand("skopa/intercept", target);
  };

  const stopIntercept = () => {
    setInterceptActive(false);
    sendCommand("skopa/abort", { track_id: target.track_id });
  };

  return (
    <Item className="w-full border-none px-0 bg-sidebar/40 last:mb-4">
      <ItemContent>
        <ItemTitle className="font-sans tracking-wide">
          {target.track_id}
        </ItemTitle>
      </ItemContent>
      <ItemActions>
        {!interceptActive && (
          <ButtonGroup>
            <Button
              onClick={() => deselect(target.track_id)}
              size="xs"
              variant="outline"
              aria-label="Deselect Target"
            >
              <IconDeselect />
              Drop
            </Button>
            <Button
              onClick={startIntercept}
              className=""
              variant="destructive"
              size="xs"
            >
              {/*<IconViewfinderOff className="text-red-500 saturate-70" />*/}
              Intercept
            </Button>
          </ButtonGroup>
        )}

        {interceptActive && (
          <Button onClick={stopIntercept} size="xs" variant="destructive">
            <IconCancel />
            Abort
            {/*Abort*/}
          </Button>
        )}
      </ItemActions>
      <ItemFooter className="border-b-2 border-dashed pb-4 pr-1 ">
        <div className="w-full font-mono text-xs text-muted-foreground brightness-140 col-span-full grid grid-cols-subgrid gap-1.5 mt-3">
          {target.kvp?.map((item: KVP) => {
            return (
              <dl key={item.key} className="grid grid-cols-2">
                <dt>{item.key}:</dt>
                <dd className="flex justify-end">{item.value}</dd>
              </dl>
            );
          })}
        </div>
      </ItemFooter>
      {interceptActive && (
        <div className="animate-pulse border border-red-500/70 saturate-70 text-center flex flex-col gap-2 justify-center items-center font-mono w-full text-xs px-2 py-8 bg-zinc-950 text-red-500 rounded mt-2">
          <IconCurrentLocation />
          Awaiting firing solution...
        </div>
      )}
    </Item>
  );
};
